import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { hitlSessionStartSeqIdCounter } from '../../app/middleware';
import {
  ICancelOrderTransmissionMessage,
  IHITLSessionEndTransmissionMessage,
  IHITLSessionStartTransmissionMessage,
  resetHypothesisFrame,
  sendCancelOrder,
  sendHITLSessionEnd,
  sendHITLSessionStart,
} from '../../reducers/messagingSlice';
import { orderActions } from '../../reducers/orderSlice';
import { getAIControlPanelStatus } from '../../reducers/restaurantSlice';
import { resetAI } from '../../redux/features/ai/ai.slice';
import {
  SessionStartReasons,
  SessionStatus,
} from '../../redux/features/sessionBoundary/sessionBoundary.constants';
import {
  selectOrderId,
  selectSessionEndReason,
  selectSessionStartReason,
  selectSessionStatus,
  selectEndedSessionId,
  selectFinalOrderSubmitted,
  selectSessionId,
} from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { setSessionStart } from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import logger from '../../utils/logger';
import useTaskRouterHandlers from '../taskRouterHandlers/taskRouterHandlers.hooks';
import { AgentTypes, EventSources, EventTypes } from '../../constants/event';
import {
  selectCurrentSessionId,
  selectIsNewSession,
  selectIsWebSocketConnected,
} from '../../selectors/message';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { post } from '../../utils/api';
import { agent } from '../../utils/agent';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { DUMMY_RESTAURANT_CODE } from '../../constants';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import { resetLoyaltiesCode } from '../../reducers/cartSlice';
import useLanguages from '../useLanguages.hooks';
import { LANGUAGES } from '../../utils/constants';
import { selectLastHITLOrderId } from '../../selectors/order';
import { selectFeatureFlagSessionEndOrderActionChanges } from '../../redux/features/config/config.selector';

const useSessionStatus = () => {
  const dispatch = useAppDispatch();
  const sessionStatus = useAppSelector(selectSessionStatus);
  const sessionStartReason = useAppSelector(selectSessionStartReason);
  const sessionEndReason = useAppSelector(selectSessionEndReason);
  const currentTaskId = useAppSelector(selectCurrentTask);
  const { endTask } = useTaskRouterHandlers();
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const wsCurrentSessionId = useAppSelector(selectCurrentSessionId);
  const sbEndedSessionId = useAppSelector(selectEndedSessionId);
  const isNewSession = useAppSelector(selectIsNewSession);
  const aiOrderId = useAppSelector(selectOrderId);
  const lastHITLOrderId = useAppSelector(selectLastHITLOrderId);
  const lastOrderId = aiOrderId || lastHITLOrderId;

  const sessionId = useAppSelector(selectSessionId);
  const userDetails = useAppSelector(selectUserProfile);
  const isWebSocketConnected = useAppSelector(selectIsWebSocketConnected);
  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const currentLanguage = useAppSelector(
    (state) => state.language.currentLanguage
  );
  const { sendLanguageSwitch } = useLanguages();
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);
  const featureFlagSessionEndOrderActionChanges = useAppSelector(
    selectFeatureFlagSessionEndOrderActionChanges
  );

  const sendHITLStartSessionViaAPI = async (data: {
    reason: string;
    brand_menu: boolean;
  }) => {
    const successCallback = (response: any) => {
      logger.info({
        message: 'Post message to Event API successfully',
        moreInfo: JSON.stringify(response),
      });
    };

    const errorCallback = (error: any) => {
      logger.error({
        message: 'Failed to send message to Event API',
        error,
      });
    };

    const { id, username, email, firstName, lastName } =
      (userDetails as UserDetails) || {};

    const payload = {
      id: uuidv4(),
      seq: hitlSessionStartSeqIdCounter.increment(),
      agent_id: agent.getAgentId(),
      agent_type: AgentTypes.HITL,
      session_id: sessionId || 'dummy_session_id',
      timestamp: new Date().toISOString(),
      restaurant_code: restaurantCode || DUMMY_RESTAURANT_CODE,
      event: EventTypes.hitlSessionStart,
      data,
      metadata: {
        agent_id: id,
        agent_name: username,
        agent_email: email,
        agent_first_name: firstName,
        agent_last_name: lastName,
      },
    };

    post({
      url: `${eventAPI}/v1/event`,
      data: {
        ...payload,
      },
      headers: {
        Authorization: `Basic ${eventAPIAuth}`,
      },
      successCallback,
      errorCallback,
    });
  };

  useEffect(() => {
    if (sessionStatus === SessionStatus.end) {
      // Switch to English when session ends if not already English
      if (currentLanguage !== LANGUAGES.ENGLISH) {
        sendLanguageSwitch(LANGUAGES.ENGLISH);
      }
      const payload: Partial<IHITLSessionEndTransmissionMessage> = {
        data: { reason: sessionEndReason || '' },
      };
      dispatch(sendHITLSessionEnd(payload as any));
      if (
        !featureFlagSessionEndOrderActionChanges &&
        lastOrderId &&
        !finalOrderSubmitted
      ) {
        logger.log(
          `Send cancel_order for order_id ${lastOrderId} when session boundary ends.`
        );
        const payload: Partial<ICancelOrderTransmissionMessage> = {
          data: {
            check_id: '-1',
            source: EventSources.prestoVoice,
            request_id: uuidv4(),
            store_id: restaurantCode,
            order_id: lastOrderId,
          },
        };
        dispatch(sendCancelOrder(payload as any));
      }
      if (
        !currentTaskId &&
        wsCurrentSessionId &&
        sbEndedSessionId &&
        wsCurrentSessionId !== sbEndedSessionId &&
        isNewSession
      ) {
        // A car enter before the current session ends so starting the new session right after the current session ends for non TR session
        logger.log(
          'Set HITL session start reason as back_to_back when session id in websocket is different from the session session boundary end'
        );
        dispatch(
          setSessionStart({
            sessionId: wsCurrentSessionId,
            sessionStartReason: SessionStartReasons.backToBack,
          })
        );
      } else {
        logger.log('Call endTask when HITL session ends in useSessionStatus');
        endTask(); // End task when a session ends
      }
    } else if (sessionStatus === SessionStatus.start) {
      const payload: Partial<IHITLSessionStartTransmissionMessage> = {
        data: {
          reason: sessionStartReason || '',
          brand_menu: getMenuUnificationFeatureFlag(),
        },
      };

      if (isWebSocketConnected) {
        dispatch(sendHITLSessionStart(payload as any));
      } else {
        sendHITLStartSessionViaAPI(payload.data as any);
      }

      if (restaurantCode && !currentTaskId) {
        dispatch(resetHypothesisFrame());
        dispatch(resetAI());
        // Get updated AI on/off status when a new HITL session starts, but don't execute when the task is received via task-router
        dispatch(getAIControlPanelStatus({ restaurantCode }));
      }
      logger.log(
        'Reset currentSession in order slice when HITL session starts in useSessionStatus'
      );
      dispatch(resetLoyaltiesCode());
      dispatch(orderActions.setCurrentSession('')); //Resetting the hitlSessionID in orderSlice whenever new session starts
    }
  }, [sessionStatus]);
};

export default useSessionStatus;
